// Removes embedded metadata (EXIF/GPS, IPTC, XMP, comments) from an image before it is
// uploaded, so learners don't unknowingly publish where a photo was taken (W-23866490).
//
// JPEG/PNG are decoded and re-drawn onto a canvas, and only the pixels are re-encoded, so
// none of the original metadata survives. EXIF orientation is applied while decoding, so
// the rotation is baked into the pixels before the orientation tag is dropped. An animated
// PNG (APNG) keeps only its first frame, and wide-gamut (Display P3) photos are converted to
// sRGB. GIFs pass through untouched: re-encoding would flatten animation, and GIFs don't
// carry camera EXIF/GPS metadata. Every other type is re-encoded, so a newly allowed format
// can't slip through with its metadata.

const PASS_THROUGH_TYPES = ['image/gif'];
const JPEG_QUALITY = 0.92;
// iOS Safari refuses to encode canvases above 16,777,216 pixels, but validation allows
// images up to 5000x5000, so larger images are scaled down to fit just under that limit.
const MAX_CANVAS_PIXELS = 16000000;

function canvasToBlob(canvas, type) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Unable to re-encode image'));
      }
    }, type, JPEG_QUALITY);
  });
}

function drawToCanvas(bitmap) {
  const scale = Math.min(1, Math.sqrt(MAX_CANVAS_PIXELS / (bitmap.width * bitmap.height)));
  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(bitmap.width * scale);
  canvas.height = Math.floor(bitmap.height * scale);
  canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return canvas;
}

// Resolves to a File with the same name and type as +file+ but without its metadata.
// Rejects if the browser can't decode or re-encode the image.
export default async function stripImageMetadata(file) {
  if (PASS_THROUGH_TYPES.includes(file.type)) {
    return file;
  }

  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  let canvas;
  try {
    canvas = drawToCanvas(bitmap);
  } finally {
    bitmap.close();
  }
  let blob;
  try {
    blob = await canvasToBlob(canvas, file.type === 'image/png' ? 'image/png' : 'image/jpeg');
  } finally {
    // Release the canvas backing store now; iOS caps total canvas memory per page.
    canvas.width = 0;
    canvas.height = 0;
  }
  // Keep the original type (including the non-standard image/jpg) so the S3 key name
  // derived from it is unchanged.
  return new File([blob], file.name, { type: file.type });
}
